import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';
import { ITripFolder } from '../../Models/trips/trip-folder.model';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { FolderType } from '../../components/trip-folders/trip-folders';
import { ITrip } from '../../Models/trips/trip.model';

class TripFolderService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  ////////////////////////////////////
  /////// Database Methods //////////
  ////////////////////////////////////
  public async getFolders(tripId: string) {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/folders`,
        { headers: authHeader() }
      );
      return response.data.data as ITripFolder[];
    } catch (error: any) {
      console.error(
        'Error fetching folders:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getFolderById(tripId: string, folderId: string) {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/folders/${folderId}`,
        { headers: authHeader() }
      );
      return response.data.data as ITripFolder;
    } catch (error: any) {
      console.error(
        'Error fetching folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createTripFolder(tripId: string, folder: any) {
    try {
      // If folder contains a parentFolderId, append it to the URL
      let url = `${this.API_URL}trips/${tripId}/folders`;
      if (folder.parentFolderId && folder.parentFolderId !== '') {
        url += `/${folder.parentFolderId}`;
      }
      const response = await axios.post(url, folder, { headers: authHeader() });
      return response.data.data as ITripFolder;
    } catch (error: any) {
      console.error(
        'Error creating folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Create a destination folder (with optional subfolders) and add it to the trip.
   * @param tripId The trip to add the destination to
   * @param destinations An object with name, placesId, latitude, longitude, and folders (array of folder types)
   */
  public async createDestinationFolder(
    tripId: string,
    destinations: {
      name?: string;
      placesId?: string;
      latitude?: number;
      longitude?: number;
      folders?: string[];
    }[]
  ) {
    try {
      console.log('sending:', destinations);
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations/folders`,
        destinations,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error creating destination folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async updateFolder(
    tripId: string,
    folderId: string,
    folder: Partial<ITripFolder>
  ) {
    try {
      const response = await axios.put(
        `${this.API_URL}trips/${tripId}/folders/${folderId}`,
        folder,
        { headers: authHeader() }
      );
      return response.data.data as ITripFolder;
    } catch (error: any) {
      console.error(
        'Error updating folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async deleteFolder(destinationId: string, folderId: string) {
    try {
      const response = await axios.delete(
        `${this.API_URL}trips/${destinationId}/folders/${folderId}`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error deleting folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async moveFolder(payload: {
    sourceTripId: string;
    sourceDestinationId?: string;
    sourceFolderId: string;
    targetTripId: string;
  }) {
    try {
      console.log('Moving folder:', payload);
      const response = await axios.post(
        this.API_URL +
          `trips/${payload.sourceTripId}/folders/${payload.sourceFolderId}/move`,
        {
          sourceDestinationId: payload.sourceDestinationId,
          targetTripId: payload.targetTripId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async copyFolder(payload: {
    sourceTripId: string;
    sourceDestinationId?: string;
    sourceFolderId: string;
    targetTripId: string;
  }) {
    try {
      const response = await axios.post(
        this.API_URL +
          `trips/${payload.sourceTripId}/folders/${payload.sourceFolderId}/copy`,
        {
          sourceDestinationId: payload.sourceDestinationId,
          targetTripId: payload.targetTripId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy folders within the same trip between destinations
   */
  public async copyFoldersWithinTrip(
    tripId: string,
    sourceDestinationId: string,
    targetDestinationId: string,
    folderIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/folders/copy-within`,
        {
          tripId,
          sourceDestinationId,
          targetDestinationId,
          folderIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying folders within trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move folders within the same trip between destinations
   */
  public async moveFoldersWithinTrip(
    tripId: string,
    sourceDestinationId: string,
    targetDestinationId: string,
    folderIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/folders/move-within`,
        {
          tripId,
          sourceDestinationId,
          targetDestinationId,
          folderIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving folders within trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy a folder to a different trip
   * Creates a selective copy of the destination with only the specified folder and its activities
   * Activities are automatically added to itinerary day 1
   */
  public async copyFolderToTrip(
    sourceTripId: string,
    targetTripId: string,
    folderId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${sourceTripId}/folders/copy-to-trip`,
        {
          sourceTripId,
          targetTripId,
          folderId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying folder to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  ////////////////////////////////////////
  /////// Helper Methods //////////
  ////////////////////////////////////////
  // public convertToFolderData(
  //   trip: ITrip,
  //   folderid: string
  // ): ITripFolderSummary | null {
  //   if (!trip || !trip.folders || trip.folders.length === 0) return null;
  //   const folder = trip.folders.find(
  //     (f) => f.id === folderid || f.id === folderid
  //   );
  //   if (!folder) return null;

  //   const childFolders = (folder.children || [])
  //     .map((child) => this.convertToFolderData(trip, String(child.id)))
  //     .filter(Boolean) as ITripFolderSummary[];

  //   return {
  //     id: String(folder.id),
  //     title: folder.title,
  //     tripId: trip.id,
  //     type: folder.type || 'folder'
  //   };
  // }

  public convertTripDestinationsToFolderTypes(trip: ITrip): FolderType[] {
    const destinations = trip.destinations || [];
    const tripActivities = trip.activities || [];

    // Each destination becomes a FolderType, with its folders/activities converted recursively
    return destinations.map((destination) => ({
      id: destination.googlePlacesId,
      destinationId: destination.googlePlacesId,
      type: 'destination',
      title: destination.name || '',
      //color: destination.color,
      place:
        destination.latitude && destination.longitude
          ? {
              placesId: destination.googlePlacesId || '',
              latitude: destination.latitude,
              longitude: destination.longitude,
              name: destination.name || ''
            }
          : null,
      folders: this.convertTripFoldersToFolderTypes(
        destination.googlePlacesId,
        [],
        tripActivities
      ),
      activities: [] // Top-level destination does not directly hold activities, only its folders do
    }));
  }

  public convertTripFoldersToFolderTypes(
    destinationId: string,
    folders: ITripFolder[] = [],
    activities: ITripActivity[] = []
  ): FolderType[] {
    const tmpFolders = folders.map((folder) => ({
      id: String(folder.id),
      destinationId: destinationId,
      type: folder.type || '', // Ensure 'type' is provided; adjust as needed
      title: folder.title,
      // color: folder.color, // if you have a color property, otherwise remove this line
      folders: this.convertTripFoldersToFolderTypes(
        destinationId,
        folder.children || [],
        activities
      ),
      activities: (folder.activities || []).map((activityId: string) => {
        // Find the full trip activity by act.id
        const fullActivity = activities.find((a) => a.id === activityId);

        return {
          id: activityId,
          type: fullActivity?.type || '', // Ensure 'type' is provided; adjust as needed
          // If 'type' is not available, you can set a default value or handle it
          title: fullActivity?.title ?? '',
          mapped:
            fullActivity?.latitude && fullActivity?.longitude ? true : false,
          description: fullActivity?.description,
          websiteUrl: fullActivity?.websiteUrl
          // Add other properties as needed, e.g.:
          // imageUrl: fullActivity?.imageUrl ?? act.imageUrl,
          // lat: fullActivity?.lat ?? act.lat,
          // long: fullActivity?.long ?? act.long,
          // color: fullActivity?.color ?? act.color, // if you have color
        };
      })
    }));
    // #TODO remove console.log('Converted trip folders to folder types:', tmpFolders);
    return tmpFolders;
  }

  /**
   * Recursively find a folder by its ID in a nested folder structure.
   * @param folders The array of folders to search in.
   * @param id The ID of the folder to find.
   * @returns The found folder or null if not found.
   */
  public findFolderById(
    folders: ITripFolder[],
    id: string
  ): ITripFolder | null {
    for (const folder of folders) {
      if (folder.id === id) {
        return folder;
      }
      if (folder.children && folder.children.length > 0) {
        const found = this.findFolderById(folder.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}

const tripFolderService = new TripFolderService();
export default tripFolderService;
